import type { ComponentProps, ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * The outer frame every route sits in.
 *
 * Density is asymmetric on purpose: rows and controls are compact, but the page
 * shell is generous. Crowding the frame is what makes dense tables feel cramped
 * rather than efficient.
 */
export function Page({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            className={cn('mx-auto flex w-full max-w-[100rem] flex-col gap-5 px-4 py-5 sm:px-6 lg:px-8', className)}
            {...props}
        />
    )
}

export interface PageHeaderProps {
    title: ReactNode
    /** One line explaining what this surface is for. Skip it when the title is self-evident. */
    description?: ReactNode
    /** Record counts, status summaries -- rendered beside the title. */
    meta?: ReactNode
    actions?: ReactNode
    className?: string
}

export function PageHeader({ title, description, meta, actions, className }: PageHeaderProps) {
    return (
        <div className={cn('flex flex-wrap items-start justify-between gap-3', className)}>
            <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-semibold leading-tight tracking-tight text-foreground">{title}</h1>
                    {meta}
                </div>
                {description && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>}
            </div>
            {/* `flex-wrap` alone could not wrap: `shrink-0` kept the group at its
                max-content width, so a header with a select and two buttons ran past the
                right edge of a phone instead of folding. Below `sm` it may shrink, which
                is what lets its own wrapping engage. */}
            {actions && (
                <div className="flex shrink-0 flex-wrap items-center gap-2 max-sm:min-w-0 max-sm:shrink">{actions}</div>
            )}
        </div>
    )
}

/** A titled region within a page. Steps one level through the surface scale. */
export function PageSection({
    title,
    description,
    actions,
    children,
    className
}: {
    title?: ReactNode
    description?: ReactNode
    actions?: ReactNode
    children: ReactNode
    className?: string
}) {
    return (
        <section className={cn('flex flex-col gap-3', className)}>
            {(title || actions) && (
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                        {title && <h2 className="text-sm font-semibold tracking-tight text-foreground">{title}</h2>}
                        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
                    </div>
                    {/* Wraps rather than overflowing: as one unbreakable line a two- or
                        three-button section header pushes the whole page sideways. */}
                    {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
                </div>
            )}
            {children}
        </section>
    )
}
